// client/src/dnd/views/Interactive_parts/useAssetEditing.js
import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import L from "leaflet";

function assetBounds(a, imageSize, fallbackSize = 256) {
  const ax = Number.isFinite(a.anchor_x) ? a.anchor_x : 0.5;
  const ay = Number.isFinite(a.anchor_y) ? a.anchor_y : 0.5;

  const w = Number(a.placement_width) || fallbackSize;
  const h = Number(a.placement_height) || fallbackSize;

  const mapW = Array.isArray(imageSize) ? Number(imageSize[0]) || 0 : 0;
  const mapH = Array.isArray(imageSize) ? Number(imageSize[1]) || 0 : 0;

  const cx = Number.isFinite(a.placement_x) ? a.placement_x : mapW / 2;
  const cy = Number.isFinite(a.placement_y) ? a.placement_y : mapH / 2;

  const x0 = cx - w * ax;
  const y0 = cy - h * ay;
  const x1 = x0 + w;
  const y1 = y0 + h;

  return [[y0, x0], [y1, x1]];
}

function useAssetEditing({ mapId, imageSize }) {
  const mapRef = useRef(null);
  const [overlayAssets, setOverlayAssets] = useState([]);
  const [imageAssets, setImageAssets] = useState([]);
  const [assetWalls, setAssetWalls] = useState([]);
  const [roofAssets, setRoofAssets] = useState([]);
  const [editingAssetId, setEditingAssetId] = useState(null);

  useEffect(() => {
    if (!mapId) {
      setAssetWalls([]);
      setRoofAssets([]);
      setOverlayAssets([]);
      setImageAssets([]);
      return;
    }

    async function loadAssetWalls() {
      try {
        const res = await fetch(`/api/tabletop/maps/${mapId}/assets?blocks_vision=true`, { credentials: "include" });
        const data = await res.json();
        if (!Array.isArray(data)) return setAssetWalls([]);
        const polys = [];
        for (const a of data) {
          try {
            const mp = JSON.parse(a.los_points || "[]");
            if (Array.isArray(mp)) {
              for (const ring of mp) {
                if (Array.isArray(ring) && ring.length > 1) {
                  polys.push(
                    ring
                      .map((p) => ({ x: Number(p.x), y: Number(p.y) }))
                      .filter((p) => Number.isFinite(p.x) && Number.isFinite(p.y))
                      .map(({ x, y }) => ({ x, y }))
                  );
                }
              }
            }
          } catch {}
        }
        setAssetWalls(polys);
      } catch {
        setAssetWalls([]);
      }
    }

    async function loadRoofAssets() {
      try {
        const res = await fetch(`/api/tabletop/maps/${mapId}/assets?type=roof_overlay`, { credentials: "include" });
        const data = await res.json();
        setRoofAssets(Array.isArray(data) ? data : []);
      } catch {
        setRoofAssets([]);
      }
    }

    async function loadOverlayAssets() {
      try {
        const res = await fetch(`/api/tabletop/maps/${mapId}/assets`, { credentials: "include" });
        const data = await res.json();
        if (!Array.isArray(data)) {
          setOverlayAssets([]);
          setImageAssets([]);
          return;
        }
        const overlays = [];
        const images = [];
        for (const a of data) {
          const t = (a.asset_type || a.type || "").toLowerCase();
          if (t === "overlay") {
            overlays.push(a);
          } else if (t === "image") {
            images.push(a);
          }
        }
        setOverlayAssets(overlays);
        setImageAssets(images);
      } catch {
        setOverlayAssets([]);
        setImageAssets([]);
      }
    }

    loadAssetWalls();
    loadRoofAssets();
    loadOverlayAssets();
  }, [mapId, imageSize]);

  useEffect(() => {
    const onAssetsUpdated = (e) => {
      const detail = e?.detail || {};
      if (detail.mapId && detail.mapId !== mapId) return;
      if (!mapId) return;
      (async () => {
        try {
          const [losRes, roofRes, allRes] = await Promise.all([
            fetch(`/api/tabletop/maps/${mapId}/assets?blocks_vision=true`, { credentials: "include" }),
            fetch(`/api/tabletop/maps/${mapId}/assets?type=roof_overlay`, { credentials: "include" }),
            fetch(`/api/tabletop/maps/${mapId}/assets`, { credentials: "include" }),
          ]);
          const [losData, roofData, allData] = [await losRes.json(), await roofRes.json(), await allRes.json()];
          const polys = [];
          if (Array.isArray(losData)) {
            for (const a of losData) {
              try {
                const mp = JSON.parse(a.los_points || "[]");
                if (Array.isArray(mp)) {
                  for (const ring of mp) {
                    if (Array.isArray(ring) && ring.length > 1) {
                      polys.push(
                        ring
                          .map((p) => ({ x: Number(p.x), y: Number(p.y) }))
                          .filter((p) => Number.isFinite(p.x) && Number.isFinite(p.y))
                          .map(({ x, y }) => ({ x, y }))
                      );
                    }
                  }
                }
              } catch {}
            }
          }
          setAssetWalls(polys);
          setRoofAssets(Array.isArray(roofData) ? roofData : []);
          if (Array.isArray(allData)) {
            const overlays = [];
            const images = [];
            for (const a of allData) {
              const t = (a.asset_type || a.type || "").toLowerCase();
              if (t === "overlay") {
                overlays.push(a);
              } else if (t === "image") {
                images.push(a);
              }
            }
            setOverlayAssets(overlays);
            setImageAssets(images);
          } else {
            setOverlayAssets([]);
            setImageAssets([]);
          }
        } catch {}
      })();
    };
    window.addEventListener("tabletop-assets-updated", onAssetsUpdated);
    return () => window.removeEventListener("tabletop-assets-updated", onAssetsUpdated);
  }, [mapId, imageSize]);

  useEffect(() => {
    const handler = (e) => {
      const detail = e?.detail || {};
      if (detail.mapId && detail.mapId !== mapId) return;
      setEditingAssetId(detail.assetId || null);
    };
    window.addEventListener("tabletop-edit-asset", handler);
    return () => window.removeEventListener("tabletop-edit-asset", handler);
  }, [mapId]);

  const editingAsset = useMemo(() => {
    if (!editingAssetId) return null;
    const all = [
      ...(overlayAssets || []),
      ...(roofAssets || []),
      ...(imageAssets || []),
    ];
    return all.find(a => a.id === editingAssetId) || null;
  }, [editingAssetId, overlayAssets, roofAssets, imageAssets]);

  const editingAssetBounds = useMemo(() => {
    if (!editingAsset) return null;
    return assetBounds(editingAsset, imageSize);
  }, [editingAsset, imageSize]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!mapRef.current || !mapId) return;
    const data =
      e.dataTransfer.getData("application/pointatlas-asset") ||
      e.dataTransfer.getData("application/json+pointatlas-asset") ||
      e.dataTransfer.getData("text/pointatlas-asset") ||
      e.dataTransfer.getData("text/plain");
    if (!data) return;
    let asset;
    try { asset = JSON.parse(data); } catch { return; }

    const container = e.currentTarget;
    const rect = container.getBoundingClientRect();
    const px = e.clientX - rect.left;
    const py = e.clientY - rect.top;

    const mapPoint = L.point(px, py);
    const latlng = mapRef.current.containerPointToLatLng(mapPoint);
    const centerY = latlng.lat;
    const centerX = latlng.lng;

    const p0 = mapRef.current.latLngToContainerPoint([centerY, centerX]);
    const p1 = mapRef.current.latLngToContainerPoint([centerY, centerX + 100]);
    const screenPerImageX = Math.max(1, Math.abs(p1.x - p0.x));
    const imagePerScreen = 1 / screenPerImageX;
    const defaultScreenSize = 200;
    const halfSizeImg = (defaultScreenSize * imagePerScreen) / 2;

    const sw = [centerY - halfSizeImg, centerX - halfSizeImg];
    const ne = [centerY + halfSizeImg, centerX + halfSizeImg];

    const width = Math.max(0.00001, ne[1] - sw[1]);
    const height = Math.max(0.00001, ne[0] - sw[0]);
    const cx = (sw[1] + ne[1]) / 2;
    const cy = (sw[0] + ne[0]) / 2;

    try {
      const payload = {
        map_id: mapId,
        asset_type: asset.asset_type || asset.type || "overlay",
        name: asset.name || `Asset #${asset.id || ""}`,
        url: asset.url,
        visible: 1,
        opacity: asset.opacity ?? 1,
        z_index: Number.isFinite(asset.z_index) ? asset.z_index : 5000,
        blocks_vision: asset.blocks_vision ?? 0,
        los_points: asset.los_points ?? null,
        anchor_x: 0.5,
        anchor_y: 0.5,
        placement_x: cx,
        placement_y: cy,
        placement_width: width,
        placement_height: height,
        rotation_deg: Number(asset.rotation_deg) || 0,
      };
      const res = await fetch(`${window.location.origin}/api/tabletop/assets`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });
      const created = await res.json();
      if (!res.ok) throw new Error(created?.error || "Add failed");
      try {
        window.dispatchEvent(new CustomEvent("tabletop-assets-updated", { detail: { mapId } }));
      } catch {}
    } catch {}
  }, [mapId]);

  const handleEditingAssetChange = useCallback(async (newBounds, rotationDeg) => {
    if (!editingAsset || !mapId || !newBounds) return;
    try {
      const [[y0, x0], [y1, x1]] = newBounds;
      const width = Math.max(0.00001, x1 - x0);
      const height = Math.max(0.00001, y1 - y0);
      const cx = (x0 + x1) / 2;
      const cy = (y0 + y1) / 2;

      const patch = {
        placement_x: cx,
        placement_y: cy,
        placement_width: width,
        placement_height: height,
      };
      if (typeof rotationDeg === "number" && !Number.isNaN(rotationDeg)) {
        patch.rotation_deg = rotationDeg;
      }

      const res = await fetch(`/api/tabletop/assets/${editingAsset.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(patch),
      });
      const data = await res.json();
      if (!res.ok) {
        console.error("Asset update failed", data?.error || data);
      }
      try {
        window.dispatchEvent(
          new CustomEvent("tabletop-assets-updated", {
            detail: { mapId },
          })
        );
      } catch {}
    } catch (err) {
      console.error("Failed to update asset", err);
    }
  }, [editingAsset, mapId]);

  return {
    mapRef,
    overlayAssets,
    imageAssets,
    roofAssets,
    assetWalls,
    editingAsset,
    editingAssetBounds,
    editingAssetId,
    handleDragOver,
    handleDrop,
    handleEditingAssetChange,
  };
}

export default useAssetEditing;
